import { urlGenerator } from "app";

import { getProviderType, fetchJson, getListData, createResponse } from "./dataProvider.utils";
import { LEGACY, MIXED, PASCAL } from "./dataProvider.types";

const dataProvider = {
  customRequest: async (url, data, method = "GET", params, useFormData = false) => {
    params ? (url = `${urlGenerator(url)}${params}`) : (url = `${urlGenerator(url)}`);

    return fetchJson({
      url,
      method,
      data,
      params: {
        meta: { useFormData: useFormData },
      },
    }).then(response => {
      // Some endpoints distinguish outcomes by status alone (e.g. 204 cancelled vs 201 pending approval).
      return { data: response.json || response.body, status: response.status };
    });
  },

  getList: async (resource, params = {}) => {
    let url = `${urlGenerator(resource)}`;
    let firstParam = false;

    if (params.pagination) {
      const { page, perPage } = params.pagination;
      firstParam = true;

      switch (getProviderType(params)) {
        case LEGACY:
          url = `${url}?_start=${perPage * (page - 1)}&_end=${perPage * page}`;
          break;
        case MIXED:
        case PASCAL:
          url = `${url}?CurrentPage=${page}&PerPage=${perPage}`;
          break;
        default:
          throw new Error("Not implemented operation");
      }
    }

    if (params.filter) {
      const filters = Object.keys(params.filter)
        .map(filterKey => {
          if (Array.isArray(params.filter[filterKey])) {
            return params.filter[filterKey].map(filterValue => `&${filterKey}=${filterValue}`).join("");
          } else {
            return `&${filterKey}=${params.filter[filterKey]}`;
          }
        })
        .join("");

      if (filters) {
        url = `${url}${filters}`;
        firstParam = true;
      }
    }

    const query = params.query || params.meta?.query;

    if (query) {
      url = `${url}${firstParam ? "&" : "?"}${query}`;
    }

    if (params.sort) {
      url = `${url}&SortBy=${params.sort.field}&SortOrder=${params.sort.order}`;
    }

    const response = await fetchJson({ url, method: "GET", params });

    return getListData({ response, params });
  },

  getOne: async (resource, params = {}) => {
    const suffix = params.meta?.suffix ? `/${params.meta.suffix}` : "";
    const id = params.id ? params.id : "";

    const url = `${urlGenerator(resource)}/${id}${suffix}`;

    return fetchJson({ url, method: "GET", params }).then(response => {
      const body = response.json;

      if (body.id === undefined) {
        body.id = body.Id;
      }

      return {
        data: body,
      };
    });
  },

  getMany: (resource, params = {}) => {
    let url = `${urlGenerator(resource)}`;
    if (params.ids) {
      url = `${url}?${params.ids.map(q => `ids=${q}`).join("&")}&PerPage=${params.ids?.length || 1}`;
    }

    const query = params.query || params.meta?.query;

    if (query) {
      url = `${url}${params.ids ? "&" : "?"}${query}`;
    }

    return fetchJson({ url, method: "GET", params }).then(response => {
      return getListData({ response, params });
    });
  },

  delete: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/${params.id}`;

    const response = await fetchJson({ url, method: "DELETE", params });
    return createResponse(response);
  },

  deleteMany: async (resource, params = {}) => {
    if (params.ids && params.ids.length > 0) {
      let url = `${urlGenerator(resource)}`;

      params.ids.forEach(async id => {
        await fetchJson({ url: `${url}/${id}`, method: "DELETE", params });
      });
    }

    return { data: params.ids || [] };
  },

  create: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}`;
    const data = params.data;

    const response = await fetchJson({ url, method: "POST", data, params });
    return createResponse(response);
  },

  update: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/${params.id}${params.meta?.suffix ? `/${params.meta.suffix}` : ""}`;

    const data = params.meta?.suffix ? { ...params.data, ...params.meta.params } : params.data;
    const response = await fetchJson({ url, method: params.meta?.method ?? "PUT", data, params });
    return createResponse(response);
  },

  getTree: async (resource, params) => {
    const url = `${urlGenerator(resource)}`;

    const response = await fetchJson({ url, method: "GET" });
    return { data: response.json };
  },

  moveAsNthChildOf: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/move-child`;

    await fetchJson({
      url,
      method: "PUT",
      data: {
        destinationId: params.destination.id,
        position: params.position,
        sourceId: params.source.id,
      },
      params: {
        meta: { useFormData: false },
      },
    });
    return { data: {} };
  },

  moveAsNthSiblingOf: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/move-sibling`;

    await fetchJson({
      url,
      method: "PUT",
      data: {
        destinationId: params.destination.id,
        position: params.position,
        sourceId: params.source.id,
      },
      params: {
        meta: { useFormData: false },
      },
    });
    return { data: {} };
  },

  addRootNode: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/root`;

    const response = await fetchJson({
      url,
      method: "POST",
      data: {
        kvp: {
          ...params.data,
          isRoot: true,
        },
      },
      params: {
        meta: { useFormData: false },
      },
    });

    return { data: response.json };
  },

  addChildNode: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/child`;

    const response = await fetchJson({
      url,
      method: "POST",
      data: {
        parentId: params.parentId,
        name: params.data.name,
        structuralUnitId: params.data.structuralUnitId,
        departmentId: params.data.departmentId,
        positionIds: params.data.positionIds,
        alternateParentId: params.data.alternateParentId,
      },
      params: {
        meta: { useFormData: false },
      },
    });
    return { data: response.json };
  },

  deleteBranch: async (resource, params = {}) => {
    const url = `${urlGenerator(resource)}/${params.id}`;

    await fetchJson({
      url,
      method: "DELETE",
    });

    return { data: {} };
  },
};

export default dataProvider;
